import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Play, GitBranch, GitMerge, Layers, Compass, Link as LinkIcon, Shuffle, Sparkles } from 'lucide-react'

const features = [
  {
    icon: GitBranch,
    title: 'Branching Narratives',
    description: 'Explore multiple coherent paths from any story point'
  },
  {
    icon: Layers,
    title: 'Segment-based Nodes',
    description: 'Build stories from reusable, modular segments'
  },
  {
    icon: GitMerge,
    title: 'Alternate Endings',
    description: 'Compare and evolve different angles and endings'
  },
  {
    icon: Compass,
    title: 'Narrative Maps',
    description: 'See the entire story tree at a glance'
  }
]

const capabilities = [
  { icon: LinkIcon, title: 'Coherence Guardrails', description: 'Keep characters, tone, and causality aligned across branches' },
  { icon: Shuffle, title: 'Branch Composer', description: 'Fork at any node to explore alternate pivots and outcomes' },
  { icon: Sparkles, title: 'AI Variations', description: 'Suggest twists, counterfactuals, and scene continuations' },
]

// Removed analytics KPIs and charts to focus on the React Flow storytelling experience

export function PlaygroundPage() {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-green-400/10 to-transparent dark:from-green-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Play className="h-6 w-6 text-green-600 dark:text-green-400" />
                Playground
              </CardTitle>
              <p className="text-muted-foreground">Non-linear storytelling platform to develop coherent story trees with branching paths and alternate endings</p>
              
              <div className="flex flex-wrap gap-2 pt-2">
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">Non‑linear Narrative</Badge>
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">Branching & Cohesion</Badge>
                <Badge variant="secondary" className="bg-muted/50 text-muted-foreground">Segment‑based Nodes</Badge>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              Coming Soon
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Main Description */
      }
      <div className="space-y-6">
        <Card className="border border-border/50 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2 text-foreground">
              <GitBranch className="h-4 w-4 text-primary" />
              Build coherent, connective stories, one branch at a time
            </CardTitle>
            <CardDescription className="text-sm sm:text-base text-muted-foreground">
              Craft narratives as trees where each node is a segment and every branch represents a complete, coherent story. Explore different turns, angles, and endings without losing context.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">


            {/* Product previews (React Flow inspiration) */}
            <Tabs defaultValue="editor" className="w-full">
              <TabsList>
                <TabsTrigger value="editor">Editor</TabsTrigger>
                <TabsTrigger value="branches">Branches</TabsTrigger>
                <TabsTrigger value="compare">Compare</TabsTrigger>
              </TabsList>

              <TabsContent value="editor" className="mt-4">
                <Card>
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-50/50 dark:bg-green-950/20 flex-shrink-0">
                        <GitBranch className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold">Story Tree Editor</CardTitle>
                        <CardDescription>
                          Visual canvas for building branching narratives
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 lg:p-8">
                    <div className="w-full flex justify-center">
                      <div className="overflow-x-auto py-4 sm:py-6 lg:py-8 max-w-full">
                        <div className="flex items-center justify-center gap-8 sm:gap-12 lg:gap-16 min-w-fit px-2 sm:px-4">
                          {/* Level 1: Root */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl border-2 border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 shadow-sm dark:shadow-primary/5">
                              <div className="text-sm font-semibold text-primary whitespace-nowrap">Premise</div>
                              <div className="text-xs text-muted-foreground mt-1 whitespace-nowrap">Starting point</div>
                            </div>
                          </div>

                          {/* Connector */}
                          <div className="flex items-center flex-shrink-0">
                            <div className="w-8 sm:w-12 h-0.5 bg-gradient-to-r from-border to-border/50 dark:from-border dark:to-border/30"></div>
                            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-border mx-1"></div>
                            <div className="w-8 sm:w-12 h-0.5 bg-gradient-to-r from-border/50 to-border dark:from-border/30 dark:to-border"></div>
                          </div>

                          {/* Level 2: Conflicts */}
                          <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8 flex-shrink-0">
                            <div className="px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg border border-muted bg-gradient-to-r from-muted/50 to-muted/20 shadow-sm dark:border-muted/50 dark:from-muted/30 dark:to-muted/10">
                              <div className="text-xs sm:text-sm font-medium text-foreground whitespace-nowrap">Conflict A</div>
                            </div>
                            <div className="px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg border border-muted bg-gradient-to-r from-muted/50 to-muted/20 shadow-sm dark:border-muted/50 dark:from-muted/30 dark:to-muted/10">
                              <div className="text-xs sm:text-sm font-medium text-foreground whitespace-nowrap">Conflict B</div>
                            </div>
                          </div>

                          {/* Connector */}
                          <div className="flex items-center flex-shrink-0">
                            <div className="w-8 sm:w-12 h-0.5 bg-gradient-to-r from-border to-border/50 dark:from-border dark:to-border/30"></div>
                            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-border mx-1"></div>
                            <div className="w-8 sm:w-12 h-0.5 bg-gradient-to-r from-border/50 to-border dark:from-border/30 dark:to-border"></div>
                          </div>

                          {/* Level 3: Endings */}
                          <div className="flex flex-col gap-2 sm:gap-3 lg:gap-4 flex-shrink-0">
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border border-border bg-accent/30 shadow-sm dark:border-border/50 dark:bg-accent/20">
                              <div className="text-xs font-medium text-foreground whitespace-nowrap">Ending A1</div>
                            </div>
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border border-border bg-accent/30 shadow-sm dark:border-border/50 dark:bg-accent/20">
                              <div className="text-xs font-medium text-foreground whitespace-nowrap">Ending A2</div>
                            </div>
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border border-border bg-accent/30 shadow-sm dark:border-border/50 dark:bg-accent/20">
                              <div className="text-xs font-medium text-foreground whitespace-nowrap">Ending B1</div>
                            </div>
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border border-border bg-accent/30 shadow-sm dark:border-border/50 dark:bg-accent/20">
                              <div className="text-xs font-medium text-foreground whitespace-nowrap">Ending B2</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="branches" className="mt-4">
                <Card className="border border-border/50">
                  <CardContent className="pt-4 space-y-3">
                    <div className="text-xs text-muted-foreground">Example story tree</div>
                    <div className="rounded-md border border-border/50 bg-muted/30 p-3 font-mono text-xs leading-6 overflow-x-auto">
{`Start: The premise
├─ Branch A: Conflict X
│  ├─ Alt Ending 1
│  └─ Alt Ending 2
└─ Branch B: Conflict Y
   └─ Alt Ending 3`}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="compare" className="mt-4">
                <Card className="border border-border/50">
                  <CardContent className="pt-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <div className="text-xs text-muted-foreground">Ending A</div>
                        <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                          Payoff centered on character growth, resolves Conflict X with a twist.
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-xs text-muted-foreground">Ending B</div>
                        <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                          Higher-stakes resolution emphasizing consequences of Conflict Y.
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      {/* What you can create */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 flex-shrink-0">
              <GitBranch className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">What you can create</CardTitle>
          <CardDescription>
                Build dynamic narratives with multiple paths and outcomes
          </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <motion.div key={feature.title} whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <Card className="group hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-muted/40 flex-shrink-0">
                        <feature.icon className="h-4 w-4 text-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold group-hover:text-primary transition-colors">
                          {feature.title}
                        </CardTitle>
                      </div>
                    </div>
        </CardHeader>
        <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50/50 dark:bg-purple-950/20 flex-shrink-0">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">How it works</CardTitle>
              <CardDescription>
                Advanced tools and AI assistance for seamless story development
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((capability) => (
              <motion.div key={capability.title} whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                <Card className="group hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted/40">
                        <capability.icon className="h-4 w-4 text-foreground" />
                      </div>
                      <CardTitle className="text-sm font-semibold group-hover:text-primary transition-colors">
                        {capability.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{capability.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 flex-shrink-0">
              <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Coming Soon</CardTitle>
              <CardDescription>
                We're building the future of interactive storytelling
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  )
}
